import { useState, useCallback } from "react";
import type { ChatMessage, CoachingStyle, PR } from "@/types/evolve";
import { supabase } from "@/integrations/supabase/client";

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/evolve-chat`;

interface UserStats {
  age?: number;
  sex?: string;
  height?: string;
  weight?: number;
  goal?: string;
  specificGoal?: string;
  experience?: string;
  daysPerWeek?: number;
  preferredTrainingTime?: string;
  equipment?: string;
  injuries?: string;
  dietPreferences?: string;
}

interface RecentWorkout {
  date: string;
  exercisesCompleted?: number;
  exercises: {
    name: string;
    sets: { reps: number; weight: number }[];
  }[];
}

interface NutritionContext {
  dailyCalorieTarget?: number;
  proteinTargetG?: number;
  carbsTargetG?: number;
  fatTargetG?: number;
  todayCalories?: number;
  todayProtein?: number;
  todayCarbs?: number;
  todayFat?: number;
  caloriesRemaining?: number;
  proteinRemaining?: number;
}

interface UserContext {
  name: string;
  daysActive: number;
  tier: string;
  prs: PR[];
  motivation?: string;
  stats?: UserStats;
  isPremium?: boolean;
  showMicronutrients?: boolean;
  activityLevel?: string;
  recentWorkoutCalories?: number;
  totalWorkouts?: number;
  recentWorkouts?: RecentWorkout[];
  // Progressive coaching context
  programStartDate?: string; // ISO date when current program started
  lastProgramChangeDate?: string; // ISO date of last program refresh
  recentPRs?: { lift: string; weight: number; date: string }[]; // PRs from last 7 days
  workoutMilestone?: 'first5' | 'first10' | 'first15' | 'first25' | 'first50' | 'legend' | null;
  // Nutrition context
  nutrition?: NutritionContext;
  // Workout streak tracking (calculated server-side for accuracy)
  currentStreak?: number; // Consecutive days with at least one workout
  lastWorkoutDaysAgo?: number; // Days since last workout (0 = today)
}

interface UseEvolveChatOptions {
  coachingStyle: CoachingStyle;
  userContext: UserContext;
}

export function useEvolveChat({ coachingStyle, userContext }: UseEvolveChatOptions) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const streamChat = useCallback(async (
    messages: ChatMessage[],
    onDelta: (deltaText: string) => void,
    onDone: () => void
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      // Context windowing: Only send the last 20 messages to AI to prevent slowdown
      // This keeps the AI fast while maintaining recent context
      const MAX_CONTEXT_MESSAGES = 20;
      const recentMessages = messages.slice(-MAX_CONTEXT_MESSAGES);
      
      // If we truncated, add a context note
      const formattedMessages = recentMessages.map(msg => ({
        role: msg.isAI ? "assistant" : "user",
        content: msg.text,
      }));

      // Get the user's session token for authenticated edge function call
      let { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Session error:', sessionError);
      }
      
      // If no session or token, try refreshing
      if (!session?.access_token) {
        console.log('No session found, attempting refresh...');
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError) {
          console.error('Session refresh error:', refreshError);
        }
        
        if (refreshData?.session?.access_token) {
          console.log('Session refreshed successfully');
          session = refreshData.session;
        } else {
          console.error('No valid session after refresh attempt');
          setError("Please log in to use the chat.");
          setIsLoading(false);
          onDone();
          return;
        }
      }
      
      console.log('Making chat request with token:', session.access_token.substring(0, 20) + '...');

      const resp = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ 
          messages: formattedMessages,
          coachingStyle,
          userContext,
          // Tell the AI if we truncated context
          truncatedHistory: messages.length > MAX_CONTEXT_MESSAGES,
        }),
      });

      console.log('Chat response status:', resp.status);

      if (resp.status === 429) {
        setError("Rate limit exceeded. Please try again in a moment.");
        setIsLoading(false);
        onDone();
        return;
      }

      if (resp.status === 402) {
        setError("AI credits exhausted. Please add credits to continue.");
        setIsLoading(false);
        onDone();
        return;
      }

      if (!resp.ok || !resp.body) {
        // Try to get error message from response
        let errorMessage = "Failed to start stream";
        try {
          const errorData = await resp.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          // Response might not be JSON
        }
        throw new Error(errorMessage);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") {
            streamDone = true;
            break;
          }

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) onDelta(content);
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }

      // Final flush
      if (textBuffer.trim()) {
        for (let raw of textBuffer.split("\n")) {
          if (!raw) continue;
          if (raw.endsWith("\r")) raw = raw.slice(0, -1);
          if (raw.startsWith(":") || raw.trim() === "") continue;
          if (!raw.startsWith("data: ")) continue;
          const jsonStr = raw.slice(6).trim();
          if (jsonStr === "[DONE]") continue;
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) onDelta(content);
          } catch { /* ignore */ }
        }
      }

      setIsLoading(false);
      onDone();
    } catch (err) {
      console.error("Chat error:", err);
      setError(err instanceof Error ? err.message : "Failed to send message");
      setIsLoading(false);
      onDone();
    }
  }, [coachingStyle, userContext]);

  return { streamChat, isLoading, error };
}
